// Audio context singleton
let audioContext: AudioContext | null = null;

const getAudioContext = (): AudioContext => {
  if (!audioContext) {
    audioContext = new AudioContext();
  }
  return audioContext;
};

// Play a simple notification chime using Web Audio API
export const playNotificationSound = async (type: 'info' | 'warning' | 'renewal' = 'info') => {
  try {
    const ctx = getAudioContext();
    
    // Resume context if suspended (browser autoplay policy)
    if (ctx.state === 'suspended') {
      await ctx.resume();
    }

    const now = ctx.currentTime;
    
    // Create oscillator for the chime
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    // Different sounds for different notification types
    if (type === 'renewal') {
      // Two-tone ascending chime for renewals
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(523.25, now); // C5
      oscillator.frequency.setValueAtTime(659.25, now + 0.15); // E5
      oscillator.frequency.setValueAtTime(783.99, now + 0.3); // G5
      
      gainNode.gain.setValueAtTime(0, now);
      gainNode.gain.linearRampToValueAtTime(0.3, now + 0.05);
      gainNode.gain.setValueAtTime(0.3, now + 0.1);
      gainNode.gain.linearRampToValueAtTime(0.25, now + 0.2);
      gainNode.gain.setValueAtTime(0.25, now + 0.35);
      gainNode.gain.linearRampToValueAtTime(0, now + 0.5);
      
      oscillator.start(now);
      oscillator.stop(now + 0.5);
    } else if (type === 'warning') {
      // Attention-grabbing double beep
      oscillator.type = 'triangle';
      oscillator.frequency.setValueAtTime(880, now); // A5
      
      gainNode.gain.setValueAtTime(0, now);
      gainNode.gain.linearRampToValueAtTime(0.25, now + 0.02);
      gainNode.gain.setValueAtTime(0.25, now + 0.1);
      gainNode.gain.linearRampToValueAtTime(0, now + 0.12);
      gainNode.gain.setValueAtTime(0, now + 0.2);
      gainNode.gain.linearRampToValueAtTime(0.25, now + 0.22);
      gainNode.gain.setValueAtTime(0.25, now + 0.32);
      gainNode.gain.linearRampToValueAtTime(0, now + 0.35);
      
      oscillator.start(now);
      oscillator.stop(now + 0.4);
    } else {
      // Simple soft chime for info
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(587.33, now); // D5
      
      gainNode.gain.setValueAtTime(0, now);
      gainNode.gain.linearRampToValueAtTime(0.2, now + 0.02);
      gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.4);
      
      oscillator.start(now);
      oscillator.stop(now + 0.4);
    }
    
    return true;
  } catch (error) {
    console.error('Error playing notification sound:', error);
    return false;
  }
};

// Check if sound can be played (user has interacted with page)
export const canPlaySound = (): boolean => {
  if (!audioContext) return true; // Will be created on first play
  return audioContext.state !== 'suspended';
};

// Initialize audio context on user interaction (call this on any user click)
export const initializeAudio = async () => {
  try {
    const ctx = getAudioContext();
    if (ctx.state === 'suspended') {
      await ctx.resume();
    }
    return true;
  } catch {
    return false;
  }
};
